/// <amd-module name="TFS/Admin/Groups/Pivots/Groups/Components/GroupsPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { IOperationStatus } from "TFS/Admin/Common/Constants/Constants";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { Avatar } from "TFS/Admin/Common/Contracts/VSS.Profile";
import { IMultiOperationDialogProps } from "TFS/Admin/Common/Controls/MultiOperationDialog/MultiOperationDialog.Props";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import {
    disableMembersLoadingFeatureFlag,
    GroupsColumns,
    IGroupDeletedValue,
    IPermissionPage,
    SearchStatus
} from "TFS/Admin/Groups/Common/Constants";
import { IGroupsViewState, PermissionsGroup } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsPivotActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsPivotActions";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { getPermissionsPage } from "TFS/Admin/Groups/Common/Util/LinkPropsHelper";
import { GroupAddPanelProps } from "TFS/Admin/Groups/Dialogs/GroupAddPanel/GroupAddPanel.Props";
import * as GroupSelectors from "TFS/Admin/Groups/Pivots/Groups/Redux/GroupsPivotSelectors";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { format, localeFormat } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Card } from "VSSUI/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { IIdentity } from "VSSUI/IdentityPicker";
import { Link } from "VSSUI/Link";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import {
    ColumnMore,
    ColumnSorting,
    ITableColumn,
    ITableRow,
    ITableRowDetails,
    SimpleTableCell,
    sortItems,
    SortOrder,
    Table,
    TableLoadingRow,
    TableRow
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

interface IGroupsPivotContainerProps extends IFilterablePivotProps {}

interface IGroupsPivotInitializerOwnProps extends IFilterablePivotOwnProps, IGroupsPivotContainerProps {
    groups: PermissionsGroup[];
    moreData: boolean;
    totalGroups: number;
    numberOfGroupsOnScreen: number;
    continutationToken?: string[];
    selectedItem: PermissionsGroup;
    selectedItemToDelete: PermissionsGroup;
    groupOperationResultMessage: string;
    groupOperationSuccess: boolean;
    groupOperationSeverity?: MessageCardSeverity;
    showMessageCard: boolean;
    loading: boolean;
    showAddGroupPanel: boolean;
    initialSelectedItem?: PermissionsGroup;
    initComplete: boolean;
    selectedIdentity: IIdentity;
    selectedGroup: PermissionsGroup;
    scopeDescriptor: string | undefined;
    isProjectScope?: boolean;
    isAadOrg: boolean;
    multiOperationStatuses: IOperationStatus[];
    isTeamsPage: boolean;
    toMarkTimeToInteract: boolean;
    permissionsGroups: IIdentity[];
    groupNameAlreadyExists: boolean;
    canEditProjectLevelInformation: boolean;
    isAddGroupDisabled: boolean;
    isCollectionNavigationAllowed: boolean;
    supportServicePrincipals: boolean;
}

export type IGroupsPivotInitializerProps = IGroupsPivotInitializerOwnProps & typeof ActionsToDispatch;

export class GroupsPivotInitializer extends FilterablePivot<IGroupsPivotInitializerProps> {
    private _selection = new ListSelection(false);
    private _isMultiSummaryOpen = new ObservableValue<boolean>(false);
    private _identity: ObservableValue<IIdentity | undefined> = new ObservableValue<IIdentity | undefined>(undefined);
    private _personaDetailsProvider: PersonaDetailsProvider;
    private _itemProvider: IObservableArray<PermissionsGroup | undefined>;
    private _currentSortOrder: SortOrder = SortOrder.descending; // Default Sort order to begin with
    private _tableColumns: ITableColumn<PermissionsGroup>[];
    private _columnSelected: number = 0;
    private _commandBarItems: IObservableArray<IHeaderCommandBarItem> = new ObservableArray<IHeaderCommandBarItem>();

    private searchDelayTimeoutId: number;

    private sortFunctions = [
        // Sort on Name column, in case the group is undefined, we push it to the end of the array.
        // An undefined group means there is a loading row to do paging.
        (item1: PermissionsGroup, item2: PermissionsGroup): number => {
            if (!item1) {
                return this._currentSortOrder === SortOrder.ascending ? 1 : -1;
            } else if (!item2) {
                return this._currentSortOrder === SortOrder.ascending ? -1 : 1;
            }
            return item2.displayName!.localeCompare(item1.displayName!, navigator.language);
        },
        null,
        // Sort on Type column, in case the group is undefined, we push it to the end of the array.
        // An undefined group means there is a loading row to do paging.
        (item1: PermissionsGroup, item2: PermissionsGroup): number => {
            if (!item1) {
                return this._currentSortOrder === SortOrder.ascending ? 1 : -1;
            } else if (!item2) {
                return this._currentSortOrder === SortOrder.ascending ? -1 : 1;
            }
            return item2.subjectKind!.localeCompare(item1.subjectKind!, navigator.language);
        },
        null
    ];

    private sortingBehavior = new ColumnSorting<any>(
        (columnIndex: number, proposedSortOrder: SortOrder, event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>) => {
            this._currentSortOrder = proposedSortOrder;
            this._columnSelected = columnIndex;

            const sortedGroups: any[] = sortItems(
                columnIndex,
                proposedSortOrder,
                this.sortFunctions,
                this._tableColumns,
                this.props.searchResult.status == SearchStatus.Completed ? this.props.searchResult.resultItems : this.props.groups
            ).slice(0, this.props.numberOfGroupsOnScreen);

            // We check for groups that have not yet fetched members that ended up on screen after sorting,
            // if there are, we fetch the members async
            const groupsWithoutMembers = sortedGroups.filter((x) => !x.members).map((x) => x);
            if (groupsWithoutMembers.length > 0) {
                this.props.updateMemberInfo(groupsWithoutMembers, false, this._getPermissionsPage());
            }

            // If we have groups not shown on screen, we added an undefined row to the array.
            if (this.props.numberOfGroupsOnScreen < this.props.groups.length) {
                const asyncRow = new ObservableValue<any | undefined>(undefined);
                sortedGroups.push(asyncRow);
            }

            this._itemProvider.value = sortedGroups;
        }
    );

    constructor(props: IGroupsPivotInitializerProps) {
        super(props);

        this.props.onDismissMessageCard();

        this._identity.value = this.props.selectedIdentity;

        this.props.setFilterPlaceholder(this.props.isTeamsPage ? Resources.FilterTeams : Resources.SearchGroupsByPrefix);
        this.props.initView(this.props.isTeamsPage);

        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);

        this._itemProvider = new ObservableArray<any>(this.props.groups);
    }

    public componentDidUpdate(prevProps: IGroupsPivotInitializerProps) {
        if (
            this.props.showMessageCard &&
            this.props.groupOperationResultMessage !== "" &&
            this.props.groupOperationSeverity === MessageCardSeverity.Info
        ) {
            sendToast(this.props.groupOperationResultMessage, this.props.pageContext);
            this.props.onDismissMessageCard();
        }

        if (this.props.toMarkTimeToInteract && !prevProps.toMarkTimeToInteract) {
            const performanceService: IVssPerformanceService = this.props.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.OrganizationGroups);
        }

        if (this.props.isProjectScope && !prevProps.isProjectScope && !this.props.isTeamsPage) {
            this._columnSelected = 2;
        }

        // Narrator not built in FilterablePivot because of the multi-render issue
        if (prevProps.searchResult && prevProps.searchResult.status != SearchStatus.Completed && this._isSearchCompleted()) {
            announce(format(Resources.SearchResultsCount, this.props.searchResult.resultItems.length));
        }

        this._setCommandBarItems();
    }

    /***Filterable Pivot Methods */
    protected _getItemsName(): string {
        return this.props.isTeamsPage ? Resources.Teams : Resources.Groups;
    }

    protected _isContentReady(): boolean {
        return !this.props.loading && !this._isFiltering();
    }

    protected _isProjectScope(): boolean {
        return this.props.isProjectScope! && !this.props.isTeamsPage;
    }

    protected _isCollectionNavigationAllowed(): boolean {
        return this.props.isCollectionNavigationAllowed;
    }

    protected _getFilteredContentColumns(): ITableColumn<PermissionsGroup>[] {
        return this._getColumns();
    }

    protected _onRenderRow(index: number, item: PermissionsGroup, details: ITableRowDetails<PermissionsGroup>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        return this._getRenderElements(this.props.groups, false);
    }

    protected _renderFilteredContent(): JSX.Element {
        return this._getRenderElements(this.props.searchResult.resultItems, true);
    }

    protected _onFilterChanged(keyword: string): void {
        clearTimeout(this.searchDelayTimeoutId);

        this.searchDelayTimeoutId = setTimeout(() => {
            this.props.logTelemetry(GroupsComponent.GroupsPivot, "Filter changed", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
            this.props.searchGroups(
                this.props.groups,
                keyword,
                this.props.searchResult ? this.props.searchResult.keyword : "",
                this.props.scopeDescriptor,
                this._getPermissionsPage()
            );
        }, Constants.searchTimeout);
    }

    protected _onExpandSearch(): void {
        this.props.logTelemetry(GroupsComponent.GroupsPivot, "Expand search", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
        this.props.searchGroups(this.props.groups, this.props.searchResult.keyword, "", "", IPermissionPage.Project);
    }

    protected _componentDidMount() {
        this.props.logTelemetry(GroupsComponent.GroupsPivot, "Pivot loaded", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
        const groupDeletedParam = this._getGroupDeletedParam();
        if (groupDeletedParam === IGroupDeletedValue.true) {
            sendToast(Resources.GroupDeletedToast, this.props.pageContext);
        }

        this.props.fetchOrgSettings();
    }

    protected _componentWillUnmount() {
        clearTimeout(this.searchDelayTimeoutId);
    }
    /***End Filterable Pivot methods */

    private _getRenderElements(items: any[], filtered: boolean): JSX.Element {
        const moreColumnKey = "_more";
        // Ensure that we load the column only once so that sortingBehavior is retained
        if (items.length > 0 && !this._tableColumns) {
            this._tableColumns = this._getColumns();
        }

        if (this._tableColumns) {
            // Type & Members column won't appear on filter
            const membersColIndex = this._tableColumns.findIndex((x) => x.id === GroupsColumns.MembersColumn);
            const typeColIndex = this._tableColumns.findIndex((x) => x.id === GroupsColumns.TypeColumn);
            const scopeColIndex = this._tableColumns.findIndex((x) => x.id === GroupsColumns.ScopeColumn);

            // When filtered, remove sorting behavior
            const nameColIndex = this._tableColumns.findIndex((x) => x.id === GroupsColumns.NameColumn);
            this._tableColumns[nameColIndex].sortProps = filtered
                ? undefined
                : { ariaLabelAscending: "Sorted A to Z", ariaLabelDescending: "Sorted Z to A" };

            if (filtered && !this.props.isTeamsPage) {
                // When filtered and not in teams page, get rid of the type column and members columns (if available)
                typeColIndex > -1 ? this._tableColumns.splice(typeColIndex, 1) : null;

                // When filtered and not in teams page, have the scope column.
                if (scopeColIndex == -1) {
                    this._tableColumns.splice(1, 0, this._getScopeColumn());
                }
            } else if (!filtered) {
                // When not filtered and in project scope, have the type column.
                if (this.props.isProjectScope && !this.props.isTeamsPage && typeColIndex == -1) {
                    this._tableColumns.splice(3, 0, this._getTypeColumn());
                }

                // When not filtered and not in teams page, remove the scope column
                if (!this.props.isTeamsPage && scopeColIndex > -1) {
                    this._tableColumns.splice(scopeColIndex, 1);
                }
            }

            // When not filtered, have the members column
            if (
                membersColIndex == -1 &&
                this.props.isTeamsPage &&
                !isFeatureFlagEnabled(this.props.pageContext, disableMembersLoadingFeatureFlag, false)
            ) {
                this._tableColumns.push(this._getMembersColumn());
            }

            if (this.props.isTeamsPage && this._tableColumns.findIndex((x) => x.id == moreColumnKey) == -1) {
                this._tableColumns.push(this._getColumnMore());
            }
            // Sort on every render based on the current sort order. This ensures that the table appears rightly sorted with every filter
            sortItems(this._columnSelected, this._currentSortOrder, this.sortFunctions, this._tableColumns, items);
        }

        const pagingItems = items.slice(0, this.props.numberOfGroupsOnScreen);

        if (this.props.numberOfGroupsOnScreen < items.length) {
            const asyncRow = new ObservableValue<any | undefined>(undefined);
            pagingItems.push(asyncRow);
        }

        this._itemProvider.value = pagingItems;

        return (
            <div>
                {this.props.showMessageCard &&
                this.props.groupOperationResultMessage != "" &&
                this.props.groupOperationSeverity !== MessageCardSeverity.Info ? (
                    <MessageCard
                        className="permissions-card"
                        severity={this.props.groupOperationSeverity}
                        onDismiss={() => this.props.onDismissMessageCard()}
                    >
                        {this.props.multiOperationStatuses.length > 0 ? (
                            <span>
                                <Link
                                    onClick={() => {
                                        this._multiSummaryOpen();
                                    }}
                                >
                                    <span
                                        dangerouslySetInnerHTML={createMarkup(
                                            this.props.groupOperationResultMessage + Resources.BulkOperationSummaryLink
                                        )}
                                    />
                                </Link>
                            </span>
                        ) : (
                            <span dangerouslySetInnerHTML={createMarkup(this.props.groupOperationResultMessage)} />
                        )}
                    </MessageCard>
                ) : null}

                {filtered && items.length === 0 ? (
                    this._getZeroData()
                ) : (
                    <Card
                        className="bolt-table-card no-rows-minimum-card-height"
                        headerCommandBarItems={this._commandBarItems}
                        contentProps={{ contentPadding: false }}
                        titleProps={{
                            text: (this._showSearchResultsCount() as unknown) as string
                        }}
                    >
                        <div className="h-scroll-auto">
                            {items.length > 0 ? (
                                <Table
                                    itemProvider={this._itemProvider}
                                    columns={this._tableColumns}
                                    onActivate={this._onRowClicked}
                                    selection={this._selection}
                                    singleClickActivation={true}
                                    behaviors={[this.sortingBehavior]}
                                    renderLoadingRow={this._renderLoadRow}
                                    className="permissions-groups-table"
                                />
                            ) : null}
                        </div>
                    </Card>
                )}

                {this.props.showAddGroupPanel ? (
                    <WrappedComponent<GroupAddPanelProps>
                        wrappedType="ms.vss-admin-web.orggroups-group-add-panel"
                        dependencies={["ms.vss-admin-web.admin-dialogs-groups-content"]}
                        wrappedProps={{
                            pageContext: this.props.pageContext,
                            onDismiss: this._onDismissAddGroupClick,
                            onSave: this._onGroupAddSaveClick,
                            onLogTelemetry: this.props.logTelemetry,
                            isAadOrg: this.props.isAadOrg,
                            isTeamsPage: this.props.isTeamsPage,
                            permissionsGroups: this.props.permissionsGroups,
                            groupNameAlreadyExists: this.props.groupNameAlreadyExists,
                            checkGroupExistence: this.props.checkGroupExistence,
                            supportServicePrincipals: this.props.supportServicePrincipals
                        }}
                    />
                ) : null}

                {
                    <Observer isMultiSummaryOpen={this._isMultiSummaryOpen}>
                        {(props: { isMultiSummaryOpen: boolean }) => {
                            return props.isMultiSummaryOpen ? (
                                <WrappedComponent<IMultiOperationDialogProps>
                                    wrappedType="ms.vss-admin-web.multi-operation-dialog"
                                    dependencies={["ms.vss-admin-web.admin-common-controls-content"]}
                                    wrappedProps={{
                                        title: Resources.ViewStatus,
                                        statusText: localeFormat(Resources.GroupAddPanelName, Resources.Team.toLocaleLowerCase()),
                                        primaryColumnHeading: Resources.Action,
                                        secondaryColumnHeading: Resources.MultiOperationStatusHeader,
                                        onCancel: this._onDismissMultiSummary,
                                        operations: this.props.multiOperationStatuses
                                    }}
                                />
                            ) : null;
                        }}
                    </Observer>
                }
            </div>
        );
    }

    private _showSearchResultsCount() {
        return (
            <div className="flex-row flex-center font-size-m" aria-live={"polite"}>
                <span>
                    {this.props.searchResult && this.props.searchResult.status != SearchStatus.Completed ? Resources.Total : Resources.SearchResults}
                </span>
                <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact} className="flex-center margin-left-4">
                    {this.props.searchResult && this.props.searchResult.status == SearchStatus.Completed
                        ? this.props.searchResult.resultItems.length
                        : this.props.groups
                        ? this.props.groups.length
                        : Resources.NoRows}
                </Pill>
            </div>
        );
    }

    private _isSearchCompleted() {
        return this.props.searchResult.status == SearchStatus.Completed;
    }

    private _onRenderMissingItem = () => {
        this.props.logTelemetry(
            GroupsComponent.GroupsPivot,
            "Load more groups on scroll",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        this.props.onRenderMissingItem(
            this.props.groups
                .slice(this.props.numberOfGroupsOnScreen, this.props.numberOfGroupsOnScreen + Constants.numberOfGroupsPerBatch)
                .filter((x) => !x.members)
                .map((x) => x),
            this.props.numberOfGroupsOnScreen,
            this._getPermissionsPage()
        );
    };

    private _renderLoadRow = (index: number, details: ITableRowDetails<PermissionsGroup>): JSX.Element => {
        this._onRenderMissingItem();
        return <TableLoadingRow columns={this._tableColumns} details={details} key={index} rowIndex={index} />;
    };

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<PermissionsGroup>) => {
        this.props.logTelemetry(GroupsComponent.GroupsPivot, "Open groups detail view", {}, "", this.props.isTeamsPage ? Feature.Teams : undefined);
        const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;

        if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
            this.props.selectedGroupChanged(tableRow.data);

            // Prevent the default navigation from taking place.
            if (event) {
                event.preventDefault();
            }
        }
    };

    private _multiSummaryOpen(): void {
        this._isMultiSummaryOpen.value = true;
    }

    private _onDismissMultiSummary = () => {
        this._isMultiSummaryOpen.value = false;
        this.props.onDismissMultiSummary();
    };

    private _getGroupDeletedParam() {
        const historyService = this.props.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const state = historyService.getState();
        if (state[Constants.GroupDeletedParam] === IGroupDeletedValue.true) {
            delete state[Constants.GroupDeletedParam];
            historyService.replaceState(state);
            return IGroupDeletedValue.true;
        }
        return IGroupDeletedValue.false;
    }

    private _onDismissAddGroupClick = () => {
        this.props.logTelemetry(
            GroupsComponent.GroupsPivot,
            "Add group panel dismiss button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        if (!this.props) {
            return null;
        }

        this.props.closeAddGroupPanel();
    };

    private _onGroupAddSaveClick = (
        displayName: string,
        description: string,
        subjectIdentities: IIdentity[],
        adminsIdentities: IIdentity[],
        adminAsMember: boolean,
        setAreaPath: boolean,
        parentGroupIdentity?: IIdentity,
        avatar?: Avatar
    ) => {
        this.props.logTelemetry(
            GroupsComponent.GroupsPivot,
            "Add group panel save button clicked",
            {},
            "",
            this.props.isTeamsPage ? Feature.Teams : undefined
        );
        if (!this.props) {
            return null;
        }

        if (avatar) {
            this.props.onAddGroupConfirm(
                displayName,
                description,
                avatar,
                subjectIdentities,
                adminsIdentities,
                adminAsMember,
                this.props.scopeDescriptor,
                this._getPermissionsPage(),
                setAreaPath,
                this.props.supportServicePrincipals,
                parentGroupIdentity
            );
        }
    };

    private _getColumns(): ITableColumn<PermissionsGroup>[] {
        const columns: ITableColumn<PermissionsGroup>[] = [];

        columns.push({
            id: "name",
            name: Resources.Name,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-35),
            renderCell: this._renderNameColumn
        });

        if (this.props.isTeamsPage) {
            columns.push({
                id: "defaultTeam",
                name: "",
                onSize: this._onSizeColumn,
                width: 90,
                minWidth: 90,
                renderCell: this._renderDefaultTeamColumn
            });
        }
        columns.push({
            id: "description",
            name: Resources.Description,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-40),
            renderCell: this._renderDescriptionColumn
        });

        return columns;
    }

    private _getTypeColumn(): ITableColumn<PermissionsGroup> {
        return {
            id: "type",
            name: Resources.Type,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-10),
            renderCell: this._renderTypeColumn,
            sortProps: { ariaLabelAscending: "Sorted by type", ariaLabelDescending: "Sorted by type" }
        };
    }

    private _getMembersColumn(): ITableColumn<PermissionsGroup> {
        return {
            id: "members",
            name: Resources.Members,
            onSize: this._onSizeColumn,
            width: 220,
            minWidth: 220,
            renderCell: this._renderMembersColumn.bind(this)
        };
    }

    private _getScopeColumn(): ITableColumn<PermissionsGroup> {
        return {
            id: "scope",
            name: Resources.Scope,
            onSize: this._onSizeColumn,
            width: new ObservableValue(-10),
            renderCell: this._renderScopeColumn.bind(this)
        };
    }

    private _getColumnMore(): ITableColumn<PermissionsGroup> {
        return new ColumnMore<any>(
            (item: PermissionsGroup) => {
                return {
                    id: "sub-menu",
                    items: [
                        {
                            id: "setProjectDefault",
                            text: Resources.SetDefaultTeam,
                            disabled: item.isDefaultTeam,
                            onActivate: () => {
                                this.props.setDefaultTeam(item.originId);
                            }
                        }
                    ]
                };
            },
            () => this.props.canEditProjectLevelInformation
        );
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element => {
        return (
            <SimpleTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                contentClassName="flex"
                className="name-cell flex flex-center"
            >
                {this._renderImageIcon(tableItem)}
                <Tooltip text={tableItem.principalName}>
                    <span className="fontWeightSemiBold text-ellipsis name-value-field">{tableItem.displayName}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private _renderScopeColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="flex">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis capitalize">{tableItem.principalName.slice(0, tableItem.principalName.indexOf("\\"))}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    };

    private _renderImageIcon(tableItem: PermissionsGroup): JSX.Element {
        return (
            <Persona
                className="display-members-persona"
                size={PersonaSize.size40}
                identity={convertGraphMemberToIIdentity(this.props.pageContext, tableItem, tableItem.updateImage)}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _renderTypeColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="flex">
                <Tooltip overflowOnly={true}>
                    <span className="text-ellipsis capitalize">{tableItem.subjectKind}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderDefaultTeamColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="flex">
                {tableItem.isDefaultTeam && <Pill>{Resources.DefaultTeam}</Pill>}
            </SimpleTableCell>
        );
    }

    private _renderDescriptionColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="flex">
                <Tooltip overflowOnly={true}>
                    <span className="let-multiline">{tableItem.description}</span>
                </Tooltip>
            </SimpleTableCell>
        );
    }

    private _renderMembersColumn(
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<PermissionsGroup>,
        tableItem: PermissionsGroup
    ): JSX.Element {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={columnIndex} contentClassName="flex">
                <div className="text-ellipsis top-level-members flex-row">
                    <div className="people">
                        {tableItem.membersCount != undefined && tableItem.membersCount >= 0 ? (
                            <Icon iconName="People" className="ms-font-l peopleIcon" />
                        ) : null}
                        <span className="ms-font-l peopleText">{tableItem.membersCount}</span>
                    </div>
                    {tableItem.members
                        ? tableItem.members.map((group, index) => (
                              <Persona
                                  key={index}
                                  className="persona-list bolt-table-cell-content-reveal"
                                  size={PersonaSize.size24}
                                  identity={convertGraphMemberToIIdentity(this.props.pageContext, group)}
                                  personaProvider={this._personaDetailsProvider}
                              />
                          ))
                        : null}
                </div>
            </SimpleTableCell>
        );
    }

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<PermissionsGroup>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _getPermissionsPage(): IPermissionPage {
        return getPermissionsPage(this.props.isTeamsPage, this.props.isProjectScope);
    }

    private _onAddGroupClick = () => {
        this.props.logTelemetry(GroupsComponent.GroupsPivot, "Add group button click", {});
        if (this.props.isTeamsPage) {
            this.props.fetchPermissionsGroups();
        } else {
            this.props.openAddGroupPanel();
        }
    };

    private _setCommandBarItems = () => {
        this._commandBarItems.value = [
            {
                important: true,
                id: "addgroup",
                ariaLabel: this.props.isTeamsPage ? Resources.NewTeamButton : Resources.NewGroupButton,
                onActivate: this._onAddGroupClick,
                text: this.props.isTeamsPage ? Resources.NewTeamButton : Resources.NewGroupButton,
                isPrimary: true,
                disabled: this.props.isAddGroupDisabled === undefined || this.props.isAddGroupDisabled
            }
        ];
    };
}

// Update component props whenever the store's state changes
function mapStateToProps(state: IGroupsViewState, providedProps: IGroupsPivotContainerProps): Partial<IGroupsPivotInitializerOwnProps> {
    return {
        ...providedProps,
        groups: GroupSelectors.getGroups(state),
        numberOfGroupsOnScreen: GroupSelectors.getNumberOfGroupsOnScreen(state),
        moreData: GroupSelectors.getMoreData(state),
        totalGroups: GroupSelectors.getTotalGroups(state),
        continutationToken: GroupSelectors.getContinuationToken(state),
        selectedItem: GroupSelectors.getSelectedItem(state),
        selectedItemToDelete: GroupSelectors.getSelectedItem(state),
        showMessageCard: GroupSelectors.getShowMessageCardStatus(state),
        groupOperationResultMessage: GroupSelectors.getGroupOperationResultMessage(state),
        groupOperationSuccess: GroupSelectors.getGroupOperationStatus(state),
        groupOperationSeverity: GroupSelectors.getGroupOperationSeverity(state),
        loading: GroupSelectors.showLoadingPanel(state),
        showAddGroupPanel: GroupSelectors.getShowAddGroupPanel(state),
        initComplete: GroupSelectors.getInitComplete(state),
        searchResult: GroupSelectors.getSearchResult(state),
        isTeamsPage: PageSelectors.getIsTeamsPageFlag(state),
        permissionsGroups: GroupSelectors.getPermissionsGroups(state),
        multiOperationStatuses: GroupSelectors.getMultiOperationStatuses(state),
        groupNameAlreadyExists: GroupSelectors.doesGroupNameAlreadyExists(state),
        canEditProjectLevelInformation: PageSelectors.canEditProjectLevelInformation(state),
        supportServicePrincipals: GroupSelectors.getSupportServicePrincipals(state),

        // Permissions view PageSelectors
        initialSelectedItem: PageSelectors.getSelectedGroup(state),
        selectedGroup: PageSelectors.getSelectedGroup(state),
        scopeDescriptor: PageSelectors.getScopeDescriptor(state),
        isCollectionNavigationAllowed: PageSelectors.isCollectionNavigationAllowed(state),
        isProjectScope: PageSelectors.isProjectScope(state),
        isAadOrg: GroupSelectors.isAadOrg(state),
        toMarkTimeToInteract: GroupSelectors.getToMarkTimeToInteract(state),
        isAddGroupDisabled: GroupSelectors.isAddGroupDisabled(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: GroupsPivotActions.initView,
    disposeView: GroupsPivotActions.disposeView,
    onAddGroupConfirm: GroupsPivotActions.onAddGroupConfirm,
    onDismissMessageCard: GroupsPivotActions.onDismissMessageCard,
    closeAddGroupPanel: GroupsPivotActions.closeAddGroupPanel,
    searchGroups: GroupsPivotActions.searchGroups,
    fetchOrgSettings: GroupsPivotActions.fetchOrgSettings,
    setDefaultTeam: GroupsPivotActions.setDefaultTeam,
    fetchPermissionsGroups: GroupsPivotActions.fetchPermissionsGroups,
    updateMemberInfo: GroupsPivotActions.updateMemberInfo,
    onRenderMissingItem: GroupsPivotActions.onRenderMissingItem,
    onDismissMultiSummary: GroupsPivotActions.onDismissMultiSummary,
    checkGroupExistence: GroupsPivotActions.checkGroupExistence,
    openAddGroupPanel: GroupsPivotActions.openAddGroupPanel,

    // Groups view actions
    selectedGroupChanged: GroupsViewActions.selectedGroupChanged,
    logTelemetry: GroupsViewActions.logTelemetry,
    setFilterPlaceholder: GroupsViewActions.setFilterPlaceholder
};

export const ConnectedGroupsPivotInitializer = connect(mapStateToProps, ActionsToDispatch, null, { withRef: true })(GroupsPivotInitializer);
